import { Divider, Paper, Typography } from "@mui/material";
import { Grid } from "@mui/system";
import React from "react";

interface GlossaryTerm {
  term: string;
  definition: React.ReactNode;
}

const terms: GlossaryTerm[] = [
  {
    term: "PS Score",
    definition: (
      <>
        PS (Prospect Savant) Score is a composite percentile that evaluates
        prospect value. It combines production, batted ball quality, and plate
        discipline — then multiplies by an age percentile (younger prospects
        score higher). A score of 70 means the player ranks in the 70th
        percentile among all qualified prospects.
      </>
    ),
  },
  {
    term: "P%",
    definition: (
      <>
        Stats labeled with "P%" are percentile stats. A value of 80 means 80%
        of qualified players rank below this player in that stat. Higher is
        always better regardless of whether the underlying stat is one where
        lower is preferred (e.g., strikeout rate).
      </>
    ),
  },
  {
    term: "Expected Weighted On-base Average (xwOBA)",
    definition: (
      <>
        xwOBA is formulated using exit velocity, launch angle, and — on certain
        batted balls — Sprint Speed. It estimates the quality of contact
        independent of defense or luck, making it a better indicator of future
        performance than traditional wOBA.
      </>
    ),
  },
  {
    term: "Agg",
    definition: (
      <>
        Agg is an unweighted average of all the player's available percentile
        stats. It gives a quick at-a-glance summary of overall player quality
        across every tracked dimension.
      </>
    ),
  },
  {
    term: "Contact Quality",
    definition: (
      <>
        Contact Quality is a composite percentile for pitchers. It aggregates
        expected slugging against, exit velocity against, hard-hit rate
        allowed, and barrel rate allowed. Higher = pitcher is suppressing
        quality contact better than most.
      </>
    ),
  },
  {
    term: "Power",
    definition: (
      <>
        Power is a composite percentile for hitters. It aggregates expected
        slugging, exit velocity, hard-hit rate, and barrel rate. It captures
        raw power output and quality of contact independent of park and defense.
      </>
    ),
  },
  {
    term: "Discipline",
    definition: (
      <>
        Discipline is a composite percentile that aggregates a player's chase
        rate (lower is better), whiff rate (lower is better), walk rate (higher
        is better), and strikeout rate (lower is better). It measures how well
        a hitter controls the strike zone.
      </>
    ),
  },
  {
    term: "Expected Batting Average (xBA)",
    definition: (
      <>
        xBA measures the likelihood that a batted ball becomes a hit based on
        exit velocity and launch angle. It strips out luck and defensive
        positioning to reflect the true quality of contact.
      </>
    ),
  },
  {
    term: "Exit Velocity (EV)",
    definition: (
      <>
        How fast, in miles per hour, a ball was hit by a batter off the bat.
        The MLB average is around 88–89 mph; elite hitters regularly average
        91+ mph. Tracked by Statcast on all batted balls.
      </>
    ),
  },
  {
    term: "Barrels",
    definition: (
      <>
        A barrel is a batted ball with an ideal combination of exit velocity
        (98+ mph) and launch angle (26–30° at 98 mph, widening as velocity
        increases). Barrels produce a batting average near .500 and a slugging
        percentage over 1.500.
      </>
    ),
  },
  {
    term: "Hard Hit",
    definition: (
      <>
        Statcast defines a hard-hit ball as one hit with an exit velocity of
        95 mph or higher. Hard-hit rate is one of the most reliable early
        indicators of a hitter's contact quality.
      </>
    ),
  },
  {
    term: "Chase %",
    definition: (
      <>
        For hitters: how often they swing at pitches outside the strike zone.
        Lower is better. For pitchers: how often they induce swings on
        pitches outside the zone. Higher is better. Elite hitters typically
        chase below 25%; elite pitchers often exceed 35%.
      </>
    ),
  },
  {
    term: "Whiff %",
    definition: (
      <>
        Swings and misses divided by total swings. For hitters, lower is
        better. For pitchers, higher is better. Whiff rate is a strong
        predictor of both strikeout rate and contact quality.
      </>
    ),
  },
  {
    term: "Z-Swing %",
    definition: (
      <>
        How often a hitter swings at pitches inside the strike zone. Higher
        is generally better for hitters — it means they're attacking
        strikes. For pitchers, it reflects how often batters offer at
        pitches in the zone.
      </>
    ),
  },
  {
    term: "Z-Contact %",
    definition: (
      <>
        How often a hitter makes contact on pitches inside the strike zone.
        Higher is better for hitters and reflects bat-to-ball ability on
        pitches they should be hitting. For pitchers, lower means they're
        generating more in-zone misses.
      </>
    ),
  },
  {
    term: "SwStr %",
    definition: (
      <>
        Swinging strike rate: swings and misses divided by total pitches
        seen (not just swings). A key proxy for strikeout rate and stuff
        quality. MLB average is roughly 11%; elite contact hitters are
        below 8%.
      </>
    ),
  },
  {
    term: "Spd",
    definition: (
      <>
        FanGraphs speed score. Incorporates stolen base success rate, stolen
        base frequency, triples rate, and runs scored rate. Scaled so that
        an average runner scores around 4–5; elite baserunners score 7+.
      </>
    ),
  },
  {
    term: "wBsR/PA",
    definition: (
      <>
        Weighted Baserunning Runs accumulated per plate appearance. Captures
        value gained or lost on the bases beyond stolen bases — taking extra
        bases, avoiding outs on the bases, etc.
      </>
    ),
  },
  {
    term: "psStuff+",
    definition: (
      <>
        Prospect Savant's pitch grading model, inspired by Eno Sarris'
        Stuff+ framework. It scores the predicted run value of a pitch based
        on similar pitches thrown at comparable velocity, movement, release
        point, and spin. 100 is league average; higher scores indicate
        better raw stuff.
      </>
    ),
  },
];

function GlossaryCard({
  term,
  definition,
  cardWidth,
  cardMargin,
}: GlossaryTerm & { cardWidth: number; cardMargin: string }) {
  return (
    <Paper
      sx={{
        maxWidth: "90vw",
        width: cardWidth,
        border: "1px solid #293241",
        borderRadius: "8px",
        padding: "16px",
        margin: cardMargin,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "#1A2027" : "#fff",
      }}
      variant="outlined"
    >
      <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
        {term}
      </Typography>
      <Divider />
      <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
        {definition}
      </Typography>
    </Paper>
  );
}

export default function Glossary({ isDesktop }: { isDesktop: boolean }) {
  const cardWidth = isDesktop ? 270 : 400;
  const cardMargin = isDesktop ? "10px" : "4px";

  return (
    <Grid container justifyContent="center" spacing={isDesktop ? 2 : 1} maxWidth="100%">
      {terms.map((t) => (
        <GlossaryCard
          key={t.term}
          term={t.term}
          definition={t.definition}
          cardWidth={cardWidth}
          cardMargin={cardMargin}
        />
      ))}
    </Grid>
  );
}
